// components/billings/expenses-list.component.jsx
import React, { useEffect, useMemo, useState } from "react";

import { FiTrash2 } from "react-icons/fi";

import {
  db,
  updateExpensePaidStatus,
} from "../../firebase/firebase.utils";

import InlineAddBtn from "../InlineAddBtn";

import {
  formatCurrencyAmount,
  formatCurrencyTotals,
  formatDocumentDate,
} from "./billing.utils";

export default function ExpensesList({
  spaceId,
  currentUserId,
  periodRange,
  onOpenExpense,
  onAddExpense,
  showDelete = true,
  setPaidExpensesByCurrency,
  setUnpaidExpensesByCurrency,
}) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!spaceId) {
      setExpenses([]);
      setLoading(false);
      return undefined;
    }

    setExpenses([]);
    setLoading(true);
    setLoadError("");

    let expensesQuery = db
      .collection("space")
      .doc(spaceId)
      .collection("expenses");

    if (periodRange?.start) {
      expensesQuery = expensesQuery.where(
        "date",
        ">=",
        periodRange.start
      );
    }

    if (periodRange?.end) {
      expensesQuery = expensesQuery.where(
        "date",
        "<=",
        periodRange.end
      );
    }

    expensesQuery = expensesQuery.orderBy("date", "desc");

    const unsubscribe = expensesQuery.onSnapshot(
      (snapshot) => {
        const data = snapshot.docs.map((document) => ({
          ...document.data(),
          id: document.id,
        }));

        setExpenses(data);
        setLoading(false);
      },
      (error) => {
        console.error("Expenses listener error:", error);
        setLoadError("Expenses could not be loaded.");
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [spaceId, periodRange]);

  const expenseSummary = useMemo(() => {
    const paid = {};
    const unpaid = {};

    expenses.forEach((expense) => {
      const currencyCode = expense.currencyCode || "USD";
      const amount = Number(expense.amount || 0);
      const destination = expense.paid ? paid : unpaid;

      destination[currencyCode] =
        Number(destination[currencyCode] || 0) + amount;
    });

    return {
      paid,
      unpaid,
    };
  }, [expenses]);

  useEffect(() => {
    setPaidExpensesByCurrency?.(expenseSummary.paid);
    setUnpaidExpensesByCurrency?.(expenseSummary.unpaid);
  }, [
    expenseSummary,
    setPaidExpensesByCurrency,
    setUnpaidExpensesByCurrency,
  ]);

  const handlePaidStatusChange = async (expense, paidStatus) => {
    if (!currentUserId) {
      window.alert("Your user information is not available.");
      return;
    }

    try {
      await updateExpensePaidStatus(
        currentUserId,
        spaceId,
        expense.id,
        paidStatus
      );
    } catch (error) {
      console.error("Expense status update error:", error);
      window.alert(
        error?.message || "The expense status could not be updated."
      );
    }
  };

  const handleDeleteExpense = async (expense) => {
    const shouldDelete = window.confirm(
      `Delete "${expense.description || "this expense"}"?`
    );

    if (!shouldDelete) return;

    try {
      await db
        .collection("space")
        .doc(spaceId)
        .collection("expenses")
        .doc(expense.id)
        .delete();
    } catch (error) {
      console.error("Delete expense error:", error);
      window.alert(
        error?.message || "The expense could not be deleted."
      );
    }
  };

  if (loading) {
    return (
      <div className="billing-loading">
        Loading expenses…
      </div>
    );
  }

  return (
    <div className="billing-list">
      <div className="expense-summary">
        <span>
          Expenses:{" "}
          <strong>
            {formatCurrencyTotals(expenseSummary.paid)}
          </strong>
        </span>

        <span>
          Pending expenses:{" "}
          <strong>
            {formatCurrencyTotals(expenseSummary.unpaid)}
          </strong>
        </span>
      </div>

      {loadError && (
        <div className="billing-error">{loadError}</div>
      )}

      {expenses.length === 0 ? (
        <div className="billing-empty billing-empty--large">
          <strong>No expenses found</strong>
          <span>
            There are no expenses recorded for this period.
          </span>
        </div>
      ) : (
        <div className="billing-section__content">
          {expenses.map((expense) => (
            <div className="billing-row" key={expense.id}>
              <button
                type="button"
                className={`billing-row__main ${
                  onOpenExpense
                    ? "billing-row__main--clickable"
                    : ""
                }`}
                onClick={() =>
                  onOpenExpense?.(expense, spaceId)
                }
              >
                <span className="billing-row__details">
                  <strong>
                    {expense.description || "Untitled expense"}
                  </strong>

                  <span className="billing-row__meta">
                    {[
                      expense.vendor,
                      expense.category,
                      formatDocumentDate(expense.date),
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </span>
                </span>

                <strong className="billing-row__amount">
                  {formatCurrencyAmount(
                    expense.currencyCode,
                    expense.amount
                  )}
                </strong>
              </button>

              {showDelete && (
                <button
                  type="button"
                  className="billing-row__icon-button"
                  aria-label={`Delete ${
                    expense.description || "expense"
                  }`}
                  onClick={() => handleDeleteExpense(expense)}
                >
                  <FiTrash2 />
                </button>
              )}

              <label className="billing-paid-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(expense.paid)}
                  onChange={(event) =>
                    handlePaidStatusChange(
                      expense,
                      event.target.checked
                    )
                  }
                />

                <span>Paid</span>
              </label>
            </div>
          ))}
        </div>
      )}
      {onAddExpense && (
  <InlineAddBtn
    className="billing-inline-add"
    circleSize={32}
    iconSize={20}
    onClick={onAddExpense}
  >
    Add expense
  </InlineAddBtn>
)}
    </div>
  );
}